<template>
    <pendo-modal
        :visible="visible"
        :title="$t('csvUploadModal.Upload list of {kinds}', { kinds: `${kind}s` })"
        :append-to-body="false"
        :show-backdrop="false"
        width="470px"
        height="auto"
        class="csv-upload-modal"
        @close="$emit('cancel')"
        @closed="reset">
        <template #body>
            <input
                ref="fileInput"
                type="file"
                hidden="true"
                @change="onFileChange($event.target)">
            <div v-if="kind == 'visitor'">
                <p>
                    {{
                        $t(
                            'csvUploadModal.Upload a CSV file to create a static segment of visitors. Each value must match exactly one visitor, or the upload will fail.',
                        )
                    }}
                </p>
                <p v-if="metadataField === 'visitorId'">
                    {{ $t("csvUploadModal.Visitors you upload who haven't used your app yet won't show as eligible in segments until they become active.") }}
                </p>
            </div>
            <p v-else>
                {{
                    $t(
                        'csvUploadModal.Select and upload a file to create segments or target guides to a specific, static group of {kinds}.',
                        { kinds: `${kind}s` },
                    )
                }}
            </p>
            <div
                v-if="state ==='ready'"
                class="csv-upload-modal__note">
                <i18n-t
                    keypath="csvUploadModal.csvModalNote"
                    tag="div">
                    <template #note>
                        <strong>{{ $t('csvUploadModal.Note:') }}</strong>
                    </template>
                </i18n-t>
            </div>
            <div
                class="flat-well"
                :class="{
                    failed: state === 'failed' || state === 'conflict',
                    success: state === 'complete',
                }">
                <div v-if="state === 'uploading'">
                    <div>
                        {{
                            $t('csvUploadModal.Uploading {fileName}...', {
                                fileName: file.name,
                            })
                        }}
                    </div>
                    <pendo-progress-bar :value="percentComplete" />
                </div>
                <pendo-alert
                    v-else-if="state === 'complete'"
                    type="success">
                    <i18n-t
                        keypath="csvUploadModal.successFileUpload"
                        tag="span">
                        <template #file>
                            <b>{{ file.name }}</b>
                        </template>
                    </i18n-t>
                </pendo-alert>
                <pendo-alert
                    v-else-if="state === 'conflict'"
                    type="error">
                    <div>
                        {{
                            $t(
                                'csvUploadModal.Another list is currently being uploaded to your account. Please retry your upload again in a few minutes.',
                            )
                        }}
                    </div>
                </pendo-alert>
                <pendo-alert
                    v-else-if="state === 'failed'"
                    type="error">
                    <div v-if="!csvLimitExceeded && !csvFieldIssueFound">
                        <i18n-t
                            v-if="metadataField === 'visitorId'"
                            keypath="csvUploadModal.csvLimitExceededVisitorId"
                            tag="div">
                            <template #file>
                                <strong>{{ file.name }}</strong>
                            </template>
                        </i18n-t>
                        <i18n-t
                            v-else
                            keypath="csvUploadModal.csvLimitExceeded"
                            tag="div">
                            <template #file>
                                <strong>{{ file.name }}</strong>
                            </template>
                            <template #description>
                                {{ $t('csvUploadModal.Each value must match exactly one {kind} in your app.', { kind: kind }) }}
                            </template>
                        </i18n-t>
                    </div>
                    <div v-if="csvLimitExceeded">
                        {{
                            $t(
                                'csvUploadModal.CSV exceeds the amount of supported entries ({limit}).',
                                { limit: csvLimit },
                            )
                        }}
                    </div>
                    <div v-if="csvFieldIssueFound">
                        <strong>{{ file.name }}</strong>: {{ csvFieldIssue }}
                    </div>
                </pendo-alert>
                <div v-else>
                    <pendo-button
                        theme="app"
                        type="primary"
                        :label="$t('csvUploadModal.Choose file')"
                        @click="chooseFile" />
                    <span v-if="file">{{ file.name }}</span>
                    <pendo-checkbox
                        v-model="excludeHeader"
                        :label="$t('csvUploadModal.Exclude row 1 (column header)')" />
                </div>
            </div>
            <pendo-alert
                v-if="uploadComplete"
                type="info">
                {{
                    $t(
                        'csvUploadModal.Only visitor IDs with activity will be included in the eligible visitors count.',
                    )
                }}
            </pendo-alert>
            <pendo-alert
                v-if="analyticsDelayed"
                type="warning">
                {{
                    $t(
                        "csvUploadModal.We're processing your CSV file. Eligible visitors appear as they are processed. Large segments may take several minutes to complete.",
                    )
                }}
            </pendo-alert>
        </template>
        <template #footer>
            <div class="csv-upload-modal__footer">
                <pendo-button
                    theme="app"
                    type="secondary"
                    :label="$t('Cancel')"
                    @click="$emit('cancel')" />
                <pendo-button
                    v-if="state === 'complete'"
                    theme="app"
                    type="primary"
                    :label="$t('csvUploadModal.Insert list')"
                    @click="insertList" />
                <pendo-button
                    v-else-if="state === 'failed'"
                    theme="app"
                    type="primary"
                    :label="$t('csvUploadModal.Upload a new file')"
                    @click="chooseFile" />
                <pendo-button
                    v-else
                    theme="app"
                    :disabled="state === 'uploading'"
                    :loading="state === 'uploading'"
                    type="primary"
                    :label="$t('csvUploadModal.Upload file')"
                    @click="uploadFile" />
            </div>
        </template>
    </pendo-modal>
</template>

<script>
import { upload, WARNING_TYPES } from '@/segments/csv-upload-service';
import { PendoAlert, PendoButton, PendoCheckbox, PendoModal, PendoProgressBar } from '@pendo/components';

export default {
    name: 'CsvUploadModal',
    components: {
        PendoProgressBar,
        PendoButton,
        PendoCheckbox,
        PendoModal,
        PendoAlert,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        kind: {
            type: String,
            required: true,
        },
        metadataField: {
            type: String,
            default: null,
        },
    },
    emits: ['cancel', 'close'],
    data () {
        return {
            state: 'ready',
            status: {},
            file: null,
            percentComplete: 0,
            excludeHeader: false,
            tagId: null,
            csvLimit: null,
            csvLimitExceeded: false,
            csvFieldIssue: null,
            csvFieldIssueFound: false,
            uploadStatusInterval: null,
        };
    },
    computed: {
        analyticsDelayed () {
            return this.status?.warnings?.some(({ type }) => type === WARNING_TYPES.ANALYTICS_DELAYED) || false;
        },
        uploadComplete () {
            return this.state === 'complete';
        },
    },
    methods: {
        chooseFile () {
            this.$refs.fileInput.click();
        },
        onFileChange (event) {
            if(this.state === 'failed') {
                this.reset(event.files[0]);
                this.uploadFile();
            }

            this.file = event.files[0];
        },
        reset (file=null) {
            window.clearInterval(this.uploadStatusInterval);
            this.state = 'ready';
            this.status = {};
            this.file = file;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
            this.percentComplete = 0;
            this.excludeHeader = false;
            this.tagId = null;
            this.csvLimitExceeded = false;
            this.csvFieldIssueFound = false;
        },
        async uploadFile () {
            const { file } = this;
            const notify = (progress) => {
                if (typeof progress === 'number') {
                    this.percentComplete = progress;
                } else if (progress.command === 'finish') {
                    this.state = 'complete';
                    this.status = progress;
                } else if (progress.command === 'error') {
                    this.handleFileUploadErrors(progress);
                }
            };

            if (file) {
                this.state = 'uploading';
                this.percentComplete = 0;

                try {
                    const uploadParams = {
                        excludeHeader: this.excludeHeader,
                    };
                    if (this.metadataField && this.metadataField !== 'visitorId') {
                        uploadParams.metadataField = this.metadataField;
                    }
                    const { entityTagId, uploadPromise, pollingInterval } = await upload(this.kind, file, notify, uploadParams);

                    this.uploadStatusInterval = pollingInterval;

                    await uploadPromise;
                    this.tagId = entityTagId;
                } catch (err) {
                    this.handleFileUploadErrors(err);
                }
            }
        },
        handleFileUploadErrors (error) {
            const { status, error: errorMessage } = error;
            switch (status) {
                case 400:
                    if (errorMessage.includes('Csv contains greater than')) {
                        try {
                            // format string as millions
                            this.csvLimit = errorMessage.match(/[0-9]+/)[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        } catch {
                            this.csvLimit = '10,000,000';
                        }
                        this.csvLimitExceeded = true;
                    }
                    if (errorMessage.includes('wrong number of fields')) {
                        try {
                            const lineMatch = errorMessage.match(/line [0-9]+/)[0];
                            this.csvFieldIssue = this.$t(
                                'csvUploadModal.Incorrect number of fields in CSV: {line}. Please check that you have {fieldCount, plural, one {# field} other {# fields}} per row and re-upload.',
                                { line: lineMatch, fieldCount: 1 },
                            );
                        } catch {
                            this.csvFieldIssue = this.$t(
                                'csvUploadModal.Incorrect number of fields in CSV. Please check that you have {fieldCount, plural, one {# field} other {# fields}} per row and re-upload.',
                                { fieldCount: 1 },
                            );
                        }
                        this.csvFieldIssueFound = true;
                    }
                    this.state = 'failed';
                    break;
                case 409:
                    this.state = 'conflict';
                    break;
                default:
                    this.state = 'failed';
            }
        },
        insertList () {
            this.$emit('close', {
                analyticsDelayedAtTimestamp: this.analyticsDelayed ? this.status.lastUpdatedAt : null,
                tagId: this.tagId,
                value: this.file.name,
            });
        },
    },
};
</script>

<style scoped lang="less">
.csv-upload-modal {
    /* Modals on drawers on modals prevent the built-in backdrop from appearing at the right z-index */
    background: rgba(var(--raw-palette-gray-100), 0.75);

    .pendo-checkbox {
        display: flex;
        width: fit-content;
        margin-top: 10px;
    }

    input[type='file'][hidden] {
        display: none;
    }

    .csv-upload-modal__note {
        margin-bottom: 10px;
    }

    .flat-well {
        margin-bottom: 0px;

        &.success,
        &.failed {
            background: none;
            padding: 0;
            border: 0;
        }

        &.failed {
            .conflict {
                > * {
                    display: inline-block;
                    vertical-align: top;
                }

                div {
                    width: 320px;
                }
            }
        }
    }

    .csv-upload-modal__footer {
        display: flex;
        justify-content: flex-end;
        flex-direction: row;
        align-items: center;
    }
}
</style>
